/**
 * 簡易 Markdown 渲染（react-markdown + remark-gfm）
 * 用於 AI 助理對話、報告檢視等需要呈現 AI 產出 markdown 的地方。
 *
 * 樣式風格保持與既有 MUI 排版一致：
 * - 字級 0.85rem
 * - 段落間距小
 * - 表格 / 清單 / 程式碼塊都收斂為內嵌風格
 */

import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, SxProps, Theme } from '@mui/material';

interface Props {
  children: string;
  sx?: SxProps<Theme>;
}

const MarkdownText: React.FC<Props> = ({ children, sx }) => {
  return (
    <Box
      className="markdown-body"
      sx={{
        fontSize: '0.85rem',
        lineHeight: 1.55,
        wordBreak: 'break-word',
        '& p':       { my: 0.5 },
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          mt: 1.5, mb: 0.5, fontWeight: 600,
        },
        '& h1': { fontSize: '1.15rem' },
        '& h2': { fontSize: '1.05rem' },
        '& h3': { fontSize: '0.95rem' },
        '& h4, & h5, & h6': { fontSize: '0.9rem' },
        '& ul, & ol': { my: 0.5, pl: 2.5 },
        '& li': { my: 0.25 },
        '& code': {
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          bgcolor: 'grey.100',
          px: 0.5, py: 0.1,
          borderRadius: 0.5,
        },
        '& pre': {
          fontFamily: 'monospace',
          fontSize: '0.78rem',
          bgcolor: 'grey.100',
          p: 1, my: 0.75,
          borderRadius: 1,
          overflowX: 'auto',
          '& code': { bgcolor: 'transparent', p: 0 },
        },
        '& blockquote': {
          borderLeft: '3px solid', borderColor: 'divider',
          pl: 1, ml: 0, my: 0.5,
          color: 'text.secondary',
        },
        '& a': { color: 'primary.main' },
        '& table': {
          borderCollapse: 'collapse', my: 0.75,
          fontSize: '0.78rem',
        },
        '& th, & td': {
          border: '1px solid', borderColor: 'divider',
          px: 0.75, py: 0.25,
        },
        '& th': { bgcolor: 'grey.100', fontWeight: 600 },
        '& hr': {
          border: 0, borderTop: '1px solid', borderColor: 'divider',
          my: 1,
        },
        '& img': { maxWidth: '100%' },
        ...sx,
      }}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {children}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownText;
